import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti-boom';
import { useAnalysis } from '../../hooks/useAnalysis';
import { useCredits } from '../../hooks/useCredits';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Link } from 'react-router-dom';

export const VideoAnalyzer = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();
  const { analysis, loading, error, startAnalysis, reset } = useAnalysis();
  const { credits } = useCredits();

  useEffect(() => {
    if (analysis && analysis.status === 'failed') {
      navigate(`/dashboard/analysis/${analysis.id}`);
    }
  }, [analysis, navigate]);

  useEffect(() => {
    if (analysis && analysis.status === 'completed' && !loading) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [analysis, loading]);

  const validateUrl = (url: string): boolean => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError('');

    if (!videoUrl.trim()) {
      setUrlError('Por favor, insira uma URL do YouTube');
      return;
    }

    if (!validateUrl(videoUrl)) {
      setUrlError('URL inválida. Por favor, insira uma URL válida do YouTube');
      return;
    }

    if (credits < 1) {
      setUrlError('Você não tem créditos suficientes. Por favor, compre créditos.');
      return;
    }

    await startAnalysis(videoUrl);
  };

  if (analysis && (analysis.status === 'completed' || analysis.status === 'failed')) {
    if (analysis.status === 'failed') {
      navigate(`/dashboard/analysis/${analysis.id}`);
      return null;
    }

    return (
      <>
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none z-50">
            <Confetti
              mode="boom"
              particleCount={50}
              colors={['#ff577f', '#ff884b', '#ffd384', '#fff9b0', '#4facfe', '#00f2fe']}
              x={0.5}
              y={0.3}
            />
          </div>
        )}
        <div className="text-center">
          <Card className="p-8">
            <h2 className="text-2xl font-bold mb-4">Análise Concluída!</h2>
            <p className="text-gray-400 mb-6">
              Sua análise está pronta. Visualize o resultado completo abaixo.
            </p>
            <Link to={`/dashboard/analysis/${analysis.id}`}>
              <Button>Ver Análise Completa</Button>
            </Link>
            <Button variant="outline" className="ml-4" onClick={reset}>
              Nova Análise
            </Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <Card className="p-8">
      <h2 className="text-2xl font-bold mb-2">Analisar Vídeo do YouTube</h2>
      <p className="text-gray-400 mb-6">
        Cole a URL do vídeo que deseja analisar. Nossa IA irá identificar erros, exageros e manipulações.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="URL do Vídeo"
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          error={urlError || error || undefined}
          disabled={loading}
        />

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Créditos disponíveis: <span className="font-semibold text-white">{credits}</span>
            {credits < 1 && (
              <Link to="/dashboard/credits" className="ml-2 text-blue-400 hover:text-blue-300">
                Comprar créditos
              </Link>
            )}
          </div>
          <Button type="submit" disabled={loading || credits < 1} className="min-h-[42px] flex items-center justify-center">
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Analisando...
              </>
            ) : (
              'Analisar Vídeo'
            )}
          </Button>
        </div>
      </form>

      {loading && analysis && (
        <div className="mt-6 p-4 card-dark">
          <div className="flex items-center space-x-3">
            <LoadingSpinner size="sm" />
            <div>
              <p className="font-semibold">Processando análise...</p>
              <p className="text-sm text-gray-400">
                Isso pode levar alguns minutos. Por favor, aguarde.
              </p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

